import type { SerializedStyles } from "@emotion/serialize";
import { useTranslation } from "react-i18next";

import { Button, Tooltip } from "@aviary/components";
import { useFlippers } from "@shared/context/FlippersContext";
import { l } from "@shared/locales/i18n";
import type { PatientAdditionalAttributesType } from "@shared/types/PatientAdditionalAttributesType";
import type { PractitionerAdditionalAttributesType } from "@shared/types/PractitionerAdditionalAttributesType";
import type { SignInAdditionalAttributesType } from "@shared/types/SignInAdditionalAttributesType";

import { OrSection } from "../../../../shared/components/LoginOptions/OrSection/OrSection";

import * as styles from "./JourneyMagicLinkButton.styles";

// URL parameter constants
const URL_PARAMS = {
  STORE_ID: "store_id",
  REDIRECT_PATH: "redirect_path",
} as const;

const MAGIC_LINK_BASE_URL = "/continue-without-password";

type AdditionalAttributes = SignInAdditionalAttributesType &
  PractitionerAdditionalAttributesType &
  PatientAdditionalAttributesType;

interface JourneyMagicLinkButtonProps {
  storeId?: string;
  disabled?: boolean;
  isOrTextRendered?: boolean;
  buttonStyles?: SerializedStyles;
  orStyles?: SerializedStyles;
  isUnauthenticated?: boolean;
  onMagicLinkClick?: () => void;
  additionalAttributes?: AdditionalAttributes;
  email?: string;
}

const JourneyMagicLinkButton = ({
  storeId,
  disabled,
  isOrTextRendered,
  buttonStyles,
  orStyles,
  isUnauthenticated,
  onMagicLinkClick,
  additionalAttributes,
  email,
}: JourneyMagicLinkButtonProps) => {
  const [isIAMEmailKillswitchEnabled] = useFlippers("iam_killswitch_disable_email_mfa_challenge");
  const { t } = useTranslation();

  // Journey-specific URL logic with redirect_path support
  const buildJourneyLinkURL = (): string => {
    const params = new URLSearchParams();

    if (storeId) {
      params.append(URL_PARAMS.STORE_ID, storeId);
    }

    if (email) {
      params.append("email", email);
    }

    const { redirectPath: additionalRedirectPath } = additionalAttributes ?? {};
    if (additionalRedirectPath) {
      params.append(URL_PARAMS.REDIRECT_PATH, additionalRedirectPath);
    }

    const queryString = params.toString();

    return queryString ? `${MAGIC_LINK_BASE_URL}?${queryString}` : MAGIC_LINK_BASE_URL;
  };

  const journeyLinkURL = buildJourneyLinkURL();

  const renderEmailButton = () => {
    if (isIAMEmailKillswitchEnabled) {
      return (
        <Tooltip tooltipContent={t(l.auth.continueWithoutPasswordDisabled)}>
          <Button data-e2e="magic-link-flow" css={[styles.magicLinkButton, buttonStyles]} disabled>
            {t(l.magicLink.ContinueWithoutPassword)}
          </Button>
        </Tooltip>
      );
    }

    // If onMagicLinkClick is provided, use it instead of navigation
    if (onMagicLinkClick) {
      return (
        <Button
          data-e2e="magic-link-flow"
          css={[styles.magicLinkButton, buttonStyles]}
          disabled={disabled}
          onClick={onMagicLinkClick}
        >
          {t(l.magicLink.ContinueWithoutPassword)}
        </Button>
      );
    }

    // Fallback to navigation for non-journey flows
    if (isUnauthenticated) {
      return (
        <Button
          data-e2e="magic-link-flow"
          css={[styles.magicLinkButton, buttonStyles]}
          disabled={disabled}
          href={journeyLinkURL}
        >
          {t(l.magicLink.ContinueWithoutPassword)}
        </Button>
      );
    }

    return (
      <Button
        data-e2e="magic-link-flow"
        css={[styles.magicLinkButton, buttonStyles]}
        disabled={disabled}
        to={journeyLinkURL}
      >
        {t(l.magicLink.ContinueWithoutPassword)}
      </Button>
    );
  };

  return (
    <div css={styles.buttonWrapper}>
      {renderEmailButton()}
      {!!isOrTextRendered && <OrSection orStyles={orStyles} />}
    </div>
  );
};

export { JourneyMagicLinkButton };
